import React, { useState } from 'react';
import { FaTimes, FaCircle, FaChevronDown, FaChevronUp, FaRedo, FaCopy, FaTrash, FaCheck, FaArrowLeft, FaArrowRight, FaCut } from 'react-icons/fa';
import HoverTooltip from '../components/tooltips/HoverTooltip.js';
import Button from '../components/buttons/Button.js';
import Toggle from '../components/Toggle/Toggle.js';
import { isElectron } from '../utils/platform.js';

export default function LiveRecorder({
  triggerRecord,
  renderRecordBtnContent,
  isUsageLimitReached,
  toggleRecordShortcut,
  isSending,
  isRecording,
  isFakeRecording,
  isLiveProcessing,
  isFormattingEnabled,
  onToggleFormatting,
  textareaVisible,
  textareaValue,
  onChangeTextarea,
  onClearTextarea,
  onCopyTextarea,
  onCutTextarea,
  onToggleTextarea,
  onNavigateBack,
  onNavigateForward,
  onReset,
  canNavigateBack,
  canNavigateForward,
  historyPosition,
  microphoneName, // Name of the current microphone device
  onClose // Optional close handler for web navigation
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await onCopyTextarea();
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // On web (when onClose is provided), hide the header
  const isWeb = !!onClose;

  return (
    <div className="electron-live-mode-container">
      {/* Header only shown on Electron */}
      {isElectron && (
        <div className="electron-live-mode-header">
          <div className="electron-live-mode-header-left">
            <Button
              icon={FaTimes}
              variant="primary"
              onClick={async () => {
                if (window?.electronAPI?.invoke) {
                  try { await window.electronAPI.invoke('liveRecorder:toggle'); } catch (_) {}
                }
              }}
              collapseOnSmallScreen={false}
              style={{ WebkitAppRegion: 'no-drag' }}
            >
            </Button>
          </div>
          <div className="electron-live-mode-header-center">
            <div className="electron-live-indicator">
              <FaCircle className="electron-live-icon" />
              <span className="electron-live-text">DICTÉE LIVE</span>
            </div>
          </div>
          <div className="electron-live-mode-header-right">

              <Button
                icon={FaRedo}
                variant="primary"
                onClick={onReset}
                collapseOnSmallScreen={false}
                style={{ WebkitAppRegion: 'no-drag' }}
              />

          </div>
        </div>
      )}

      <div className="electron-live-mode-content">
        <div
          className="electron-live-mode-controls"
          style={isElectron && !textareaVisible ? { justifyContent: 'center', flex: 1 } : {}}
        >
          {/* Textarea area at top - always visible on web, toggleable on Electron */}
          {(!isElectron || textareaVisible) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%', flex: 1, minHeight: 0 }}>
              {/* Icon buttons on the right with history position */}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
                {historyPosition && (
                  <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, marginRight: 4 }}>
                    {historyPosition}
                  </span>
                )}

                <HoverTooltip text={copied ? "Copié !" : "Copier"} position="top" forceShow={true}>
                  <button
                    onClick={handleCopy}
                    className="live-icon-button"
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      border: 'none',
                      background: copied ? 'var(--color-success, #28a745)' : '#f0f0f0',
                      color: copied ? '#fff' : '#586069',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    {copied ? <FaCheck size={12} /> : <FaCopy size={12} />}
                  </button>
                </HoverTooltip>

                <HoverTooltip text="Couper" position="top" forceShow={true}>
                  <button
                    onClick={onCutTextarea}
                    className="live-icon-button"
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      border: 'none',
                      background: '#f0f0f0',
                      color: '#586069',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    <FaCut size={12} />
                  </button>
                </HoverTooltip>

                  <button
                    onClick={onClearTextarea}
                    className="live-icon-button"
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      border: 'none',
                      background: '#f0f0f0',
                      color: '#586069',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    <FaTrash size={12} />
                  </button>

                <HoverTooltip text="Précédent" position="top" forceShow={true}>
                  <button
                    onClick={onNavigateBack}
                    disabled={!canNavigateBack}
                    className="live-icon-button"
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      border: 'none',
                      background: '#f0f0f0',
                      color: '#586069',
                      cursor: canNavigateBack ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      opacity: canNavigateBack ? 1 : 0.4
                    }}
                  >
                    <FaArrowLeft size={12} />
                  </button>
                </HoverTooltip>

                <HoverTooltip text="Suivant" position="left" forceShow={true}>
                  <button
                    onClick={onNavigateForward}
                    disabled={!canNavigateForward}
                    className="live-icon-button"
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      border: 'none',
                      background: '#f0f0f0',
                      color: '#586069',
                      cursor: canNavigateForward ? 'pointer' : 'not-allowed',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s ease',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      opacity: canNavigateForward ? 1 : 0.4
                    }}
                  >
                    <FaArrowRight size={12} />
                  </button>
                </HoverTooltip>
              </div>

              <textarea
                style={{
                  flex: 1,
                  minHeight: 150,
                  resize: 'none',
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  lineHeight: '1.5',
                  color: '#374151',
                  outline: 'none',
                  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)'
                }}
                value={textareaValue}
                onChange={(e) => onChangeTextarea && onChangeTextarea(e.target.value)}
                placeholder="Texte dicté..."
              />
            </div>
          )}

          {/* Bottom controls: Record button + Toggle centered */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, width: '100%', paddingTop: 16, paddingBottom: 8 }}>
            <div className="electron-live-buttons-container" style={{ marginTop: 0 }}>
              <HoverTooltip
                text={(() => {
                  const parts = [];
                  if (microphoneName) parts.push(`🎤 ${microphoneName}`);
                  if (toggleRecordShortcut) parts.push(toggleRecordShortcut);
                  return parts.join(' • ') || '';
                })()}
                position="top"
                forceShow={true}
              >
                <button
                  className={'electron-record-button'}
                  onClick={triggerRecord}
                  disabled={isUsageLimitReached}
                  style={{
                    background: isFormattingEnabled ? 'var(--gradient-success)' : undefined
                  }}
                >
                  {renderRecordBtnContent()}
                </button>
              </HoverTooltip>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 10, color: '#586069', fontWeight: 600 }}>Mettre en forme</span>
              <Toggle
                checked={isFormattingEnabled}
                onChange={onToggleFormatting}
              />
            </div>
            {/* Chevron toggle button - only on Electron */}
            {isElectron && (
              <button
                onClick={onToggleTextarea}
                className="live-icon-button"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: 'none',
                  background: '#f0f0f0',
                  color: '#586069',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                {textareaVisible ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Double-tap prompt removed */}
    </div>
  );
}

 